import React, { useEffect } from 'react';
import styled from 'styled-components';
import Routes from 'Routes';
import { useTranslation } from 'react-i18next';
import { Button, withStyles } from '@material-ui/core';
import CircularLoadingIndicator from 'components/indicators/CircularLoadingIndicator';
import FloatingFooter from 'components/footer_v2/FloatingFooter';
import ErrorBotIcon from 'components/icons/ErrorBot';
import { Table } from 'models/networking_lounge/types';
import { fetchTables, selectAllTables } from 'models/networking_lounge/tables';
import useInterval from 'hooks/useInterval';
import { useDispatch, useSelector } from 'react-redux';
import TableItem from './TableItem';

const RootContainer = styled.div`
  display: flex;
  width: 100%;
  min-height: 100%;
  overflow: auto;
  flex-direction: column;
`;

const TableListContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  padding: 8px 16px;
  width: 100%;
  max-width: 100%;
  height: 100%;
`;

const EmptyStateContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-color: rgba(0, 0, 0, 0.6);
`;

const EmptyStateTitle = styled.div`
  font-weight: 700;
  font-size: 32px;
  line-height: 44px;
  color: #FFF;
  padding: 24px;
`;

const EmptyStateText = styled.div`
  font-weight: 700;
  font-size: 24px;
  line-height: 33px;
  color: #FFF;
  padding: 24px;
`;

const FooterContainer = styled.div`
  flex-basis: 100%;
  margin-bottom: 16px;
`;

const NetworkingLoungeLoadingContainer = styled.div`
  flex-basis: 100%;
`;

const TitleContainer = styled.div`
  margin-top: 40px;
  margin-bottom: 40px;

  @media (min-width: 960px) {
    margin-top: 120px;
    margin-bottom: 70px;
  }
`;

const WelcomeTitle = styled.div`
  color: #FFF;
  font-weight: 700;
  font-size: 22px;
  line-height: 26.4px;
  text-align: center;
  padding: 16px;

  @media (min-width: 960px) {
    font-size: 48px;
    line-height: 60px;
  }
`;

const WelcomeText = styled.div`
  color: #FFF;
  font-size: 14px;
  line-height: 16.8px;
  text-align: center;

  @media (min-width: 960px) {
    font-size: 24px;
    line-height: 28.8px;
  }
`;

const StyledButton = withStyles({
  root: {
    backgroundColor: '#D80C24',
    color: '#FFF',
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '100%',
    textTransform: 'none',
    padding: '16px 24px',
    '&:hover': {
      backgroundColor: '#D80C24',
      color: '#FFF',
    },
  },
})(Button);

const NetworkingLoungeLoadingIndicator = () => (
  <NetworkingLoungeLoadingContainer>
    <CircularLoadingIndicator />
  </NetworkingLoungeLoadingContainer>
);

const TableList = () => {
  const dispatch = useDispatch();
  const tableList: Table[] = useSelector(selectAllTables);
  const { t } = useTranslation();
  const fetching = useSelector((state) => state.networkingLounge.tables.fetching);
  const fetchError = useSelector((state) => state.networkingLounge.tables.fetchError);

  useEffect(() => {
    dispatch(fetchTables());
  }, [dispatch]);

  useInterval(() => {
    dispatch(fetchTables());
  }, fetching && !fetchError ? null : 60000);

  const onRetryNowBtnClick = () => {
    dispatch(fetchTables());
  };

  return (
    <RootContainer>
      {
        !fetchError && (
          <>
            <TitleContainer>
              <WelcomeTitle>
                {t('networking_lounge.title', 'Welcome to Networking Lounge')}
              </WelcomeTitle>
              <WelcomeText>
                {t('networking_lounge.description', 'Meet and greet new friends by topics')}
              </WelcomeText>
            </TitleContainer>
            <TableListContainer>
              {!!tableList.length && !fetching && (
                <>
                  {tableList.map((table, index) => (
                    <TableItem table={table} key={table.id} index={index} />
                  ))}
                </>
              )}
              {fetching && (
                <NetworkingLoungeLoadingIndicator />
              )}
              <FooterContainer>
                <FloatingFooter hashState={Routes.networkingLounge} />
              </FooterContainer>
            </TableListContainer>
          </>
        )
      }
      {fetchError && (
        <EmptyStateContainer>
          <ErrorBotIcon width="160px" height="160px" color="#FFF" />
          <EmptyStateTitle>{t('networking_lounge.something_went_wrong', 'Oops! Something has gone wrong!')}</EmptyStateTitle>
          <EmptyStateText>
            {t('networking_lounge.empty', 'No available table found')}
          </EmptyStateText>
          <StyledButton onClick={onRetryNowBtnClick}>
            {t('networking_lounge.retry', 'Retry Now')}
          </StyledButton>
        </EmptyStateContainer>
      )}
    </RootContainer>
  );
};

export default TableList;
